import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { Portal } from "./portal"

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const monthFormatter = new Intl.DateTimeFormat("en", { month: "long", year: "numeric" })
const longDateFormatter = new Intl.DateTimeFormat("en", { dateStyle: "long" })
const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function dateKey(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${date.getFullYear()}-${month}-${day}`
}

function displayDate(date: Date | null) {
  if (!date) return ""
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${day}/${month}/${date.getFullYear()}`
}

function calendarDays(month: Date) {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1 - month.getDay())
  return Array.from({ length: 42 }, (_, index) => new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate() + index))
}

function addDays(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount)
}

function isDateDisabled(date: Date, min: Date | null, max: Date | null) {
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  if (min) {
    const minOnly = new Date(min.getFullYear(), min.getMonth(), min.getDate())
    if (dateOnly < minOnly) return true
  }
  if (max) {
    const maxOnly = new Date(max.getFullYear(), max.getMonth(), max.getDate())
    if (dateOnly > maxOnly) return true
  }
  return false
}

function getYearRange(min: Date | null, max: Date | null) {
  const currentYear = new Date().getFullYear()
  const minYear = min ? min.getFullYear() : currentYear - 100
  const maxYear = max ? max.getFullYear() : currentYear + 10
  const years: number[] = []
  for (let year = maxYear; year >= minYear; year--) {
    years.push(year)
  }
  return years
}

interface DatePickerProps {
  label?: string
  value: string // ISO date string (YYYY-MM-DD) or empty
  onChange: (value: string) => void
  disabled?: boolean
  min?: Date | null
  max?: Date | null
  placeholder?: string
  popoverAlign?: "left" | "right"
}

export function DatePicker({
  label,
  value,
  onChange,
  disabled = false,
  min = null,
  max = null,
  placeholder = "dd/mm/yyyy",
  popoverAlign = "left",
}: DatePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const selected = value ? new Date(value) : null
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected || new Date()))
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<"days" | "months" | "years">("days")
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null)
  const days = calendarDays(viewMonth)
  const years = getYearRange(min, max)

  useEffect(() => {
    if (!open) return

    function handleOutsidePointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (!containerRef.current?.contains(target) && !calendarRef.current?.contains(target)) setOpen(false)
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (view !== "days") {
          setView("days")
        } else {
          setOpen(false)
          buttonRef.current?.focus()
        }
      }
    }

    document.addEventListener("pointerdown", handleOutsidePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [open, view])

  useLayoutEffect(() => {
    if (!open) {
      setPopoverPosition(null)
      return
    }

    function positionPopover() {
      const trigger = buttonRef.current
      const calendar = calendarRef.current
      if (!trigger || !calendar) return

      const gap = 8
      const viewportPadding = 12
      const triggerRect = trigger.getBoundingClientRect()
      const calendarWidth = calendar.offsetWidth
      const calendarHeight = calendar.offsetHeight
      const spaceBelow = window.innerHeight - triggerRect.bottom - gap - viewportPadding
      const spaceAbove = triggerRect.top - gap - viewportPadding
      const openAbove = spaceBelow < calendarHeight && spaceAbove > spaceBelow
      const preferredTop = openAbove ? triggerRect.top - calendarHeight - gap : triggerRect.bottom + gap
      const maxTop = Math.max(viewportPadding, window.innerHeight - calendarHeight - viewportPadding)
      const top = Math.max(viewportPadding, Math.min(preferredTop, maxTop))
      const preferredLeft = popoverAlign === "right" ? triggerRect.right - calendarWidth : triggerRect.left
      const maxLeft = Math.max(viewportPadding, window.innerWidth - calendarWidth - viewportPadding)
      const left = Math.max(viewportPadding, Math.min(preferredLeft, maxLeft))

      setPopoverPosition({ top, left })
    }

    positionPopover()
    window.addEventListener("resize", positionPopover)
    window.addEventListener("scroll", positionPopover, true)
    return () => {
      window.removeEventListener("resize", positionPopover)
      window.removeEventListener("scroll", positionPopover, true)
    }
  }, [open, popoverAlign, view])

  function openPicker() {
    if (disabled) return
    if (selected) setViewMonth(startOfMonth(selected))
    setView("days")
    setOpen(true)
  }

  function selectDate(date: Date) {
    onChange(dateKey(date))
    setViewMonth(startOfMonth(date))
    setOpen(false)
    buttonRef.current?.focus()
  }

  function focusDate(date: Date) {
    setViewMonth(startOfMonth(date))
    window.setTimeout(() => {
      calendarRef.current?.querySelector<HTMLButtonElement>(`[data-date="${dateKey(date)}"]`)?.focus()
    })
  }

  function handleButtonKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault()
      openPicker()
    }
  }

  function handleDateKeyDown(date: Date, event: React.KeyboardEvent<HTMLButtonElement>) {
    const movement: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    }
    const amount = movement[event.key]

    if (amount) {
      event.preventDefault()
      let nextDate = addDays(date, amount)
      const step = amount > 0 ? 1 : -1
      while (isDateDisabled(nextDate, min, max)) {
        nextDate = addDays(nextDate, step)
        if (Math.abs(nextDate.getTime() - date.getTime()) > 365 * 24 * 60 * 60 * 1000) break
      }
      if (!isDateDisabled(nextDate, min, max)) {
        focusDate(nextDate)
      }
      return
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      if (!isDateDisabled(date, min, max)) {
        selectDate(date)
      }
    }
  }

  return (
    <div className="relative">
      {label && <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>}
      <div ref={containerRef} className="relative">
        <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={openPicker}
          onKeyDown={handleButtonKeyDown}
          className={`flex w-full items-center justify-between rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-left text-sm transition-colors hover:border-ink/30 focus:border-forest-700 focus:outline-none disabled:opacity-60 ${
            selected ? "text-ink" : "text-ink/40"
          }`}
        >
          <span>{selected ? displayDate(selected) : placeholder}</span>
          <svg viewBox="0 0 24 24" className="h-5 w-5 text-ink/50" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
            <path d="M7.5 3.5v4M16.5 3.5v4M3.5 9.5h17" />
          </svg>
        </button>

        {open && (
          <Portal>
            <div
              ref={calendarRef}
              role="dialog"
              aria-label="Choose date"
              onClick={(event) => event.stopPropagation()}
              style={{ top: popoverPosition?.top ?? 0, left: popoverPosition?.left ?? 0 }}
              className={`fixed z-[60] max-h-[calc(100vh-1.5rem)] w-[min(20rem,calc(100vw-2.5rem))] overflow-y-auto rounded-lg border border-gold-500 bg-white p-3 shadow-lg ${popoverPosition ? "" : "invisible"}`}
            >
            {view === "days" && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    aria-label="Previous month"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m10 3-5 5 5 5" />
                    </svg>
                  </button>
                  <div className="flex items-center gap-1">
                    <span className="sr-only">{monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
                    <button
                      type="button"
                      onClick={() => setView("months")}
                      className="rounded px-1.5 py-0.5 text-sm font-semibold text-ink hover:bg-cream-100 focus:outline-none focus:ring-0"
                    >
                      {monthNames[viewMonth.getMonth()]}
                    </button>
                    <button
                      type="button"
                      onClick={() => setView("years")}
                      className="rounded px-1.5 py-0.5 text-sm font-semibold text-ink hover:bg-cream-100 focus:outline-none focus:ring-0"
                    >
                      {viewMonth.getFullYear()}
                    </button>
                  </div>
                  <button
                    type="button"
                    aria-label="Next month"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m6 3 5 5-5 5" />
                    </svg>
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-7 text-center text-[11px] font-medium text-ink/50" aria-hidden="true">
                  {weekdays.map((weekday) => <span key={weekday}>{weekday}</span>)}
                </div>
                <div role="grid" aria-label={monthFormatter.format(viewMonth)} className="mt-1 grid grid-cols-7 gap-1">
                  {days.map((date) => {
                    const currentMonth = date.getMonth() === viewMonth.getMonth()
                    const isSelected = selected ? dateKey(date) === dateKey(selected) : false
                    const isToday = dateKey(date) === dateKey(new Date())
                    const isDisabled = isDateDisabled(date, min, max)
                    return (
                      <button
                        key={dateKey(date)}
                        type="button"
                        role="gridcell"
                        data-date={dateKey(date)}
                        aria-label={longDateFormatter.format(date)}
                        aria-selected={isSelected}
                        aria-disabled={isDisabled}
                        disabled={isDisabled}
                        onClick={() => selectDate(date)}
                        onKeyDown={(event) => handleDateKeyDown(date, event)}
                        className={`grid h-8 place-items-center rounded text-xs transition-colors focus:outline-none focus:ring-0 ${
                          isDisabled
                            ? "cursor-not-allowed text-ink/20"
                            : isSelected
                              ? "bg-forest-950 font-semibold text-white"
                              : isToday
                                ? "font-semibold text-forest-700 hover:bg-cream-100"
                                : currentMonth
                                  ? "text-ink hover:bg-cream-100"
                                  : "text-ink/30 hover:bg-cream-100"
                        }`}
                      >
                        {date.getDate()}
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {view === "months" && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    aria-label="Previous year"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear() - 1, month.getMonth(), 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m10 3-5 5 5 5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("years")}
                    className="rounded px-2 py-0.5 text-sm font-semibold text-ink hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    {viewMonth.getFullYear()}
                  </button>
                  <button
                    type="button"
                    aria-label="Next year"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear() + 1, month.getMonth(), 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m6 3 5 5-5 5" />
                    </svg>
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {monthNames.map((month, index) => (
                    <button
                      key={month}
                      type="button"
                      onClick={() => { setViewMonth(new Date(viewMonth.getFullYear(), index, 1)); setView("days") }}
                      className={`rounded px-2 py-2 text-sm transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0 ${
                        index === viewMonth.getMonth() ? "font-semibold text-forest-700" : "text-ink"
                      }`}
                    >
                      {month.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </>
            )}

            {view === "years" && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    aria-label="Previous years"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear() - 12, month.getMonth(), 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m10 3-5 5 5 5" />
                    </svg>
                  </button>
                  <span className="text-sm font-semibold text-ink">Select year</span>
                  <button
                    type="button"
                    aria-label="Next years"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear() + 12, month.getMonth(), 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m6 3 5 5-5 5" />
                    </svg>
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {(() => {
                    const startYear = Math.floor(viewMonth.getFullYear() / 12) * 12
                    return Array.from({ length: 12 }, (_, i) => startYear + i)
                      .filter((year) => years.includes(year))
                      .map((year) => (
                        <button
                          key={year}
                          type="button"
                          onClick={() => { setViewMonth(new Date(year, viewMonth.getMonth(), 1)); setView("months") }}
                          className={`rounded px-2 py-2 text-sm transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0 ${
                            year === viewMonth.getFullYear() ? "font-semibold text-forest-700" : "text-ink"
                          }`}
                        >
                          {year}
                        </button>
                      ))
                  })()}
                </div>
              </>
            )}
            </div>
          </Portal>
        )}
      </div>
    </div>
  )
}
